import { Component } from "@angular/core";
import { ActivatedRoute, Router } from '@angular/router';
import { I18nService } from '@/core/services/i18nService';
import { LoadingAnimationService } from '../../../../services/loading-animation.service';
import { GutachtenDisplayModel, GutachtenDisplayModelFormGroup } from '../../../../../api/models/ace/xFound/Portal/Core/CAQ/Models/gutachten-display-model';
import { UnfinishedHttpMethodsService } from '../../../../services/unfinished-http-methods.service';
import { AnfrageNavigationFormComponent, MoveDirection } from '@/core/components/anfrage-navigation-form/anfrage-navigation-form.component';
import { FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { PortalContextService } from '../../../../services/portal-context.service';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { SectionService } from '@/core/services/section.service';
import { StateService } from "@/core/services/state.service";
import { AnfrageService, GutachtenService } from "@/api/service-index";
import { GlobalEnumsAceBewertung } from "@/api/model-index";
import { EnumValue } from "@/core/models/enumvalue.model";
import { Constants } from "@/constants";
import { CKEditorService } from "@/core/services/ckeditor.service";
import * as CustomEditor from '@ckeditor/ckeditor5-build-classic';

@Component({
    selector: 'review-detail',
    templateUrl: './review-detail.component.html',
    styleUrls: ['./review-detail.component.scss']
})
export class ReviewDetail extends AnfrageNavigationFormComponent {
    public review: GutachtenDisplayModel;
    public reviewForm: GutachtenDisplayModelFormGroup;

    protected submitReviewDialog: DialogRef;
    protected reviewDisabledDialog: DialogRef;

    constructor(protected router: Router,
        protected route: ActivatedRoute,
        protected languageservice: I18nService,
        protected loadingAnimationService: LoadingAnimationService,
        protected gutachtenService: GutachtenService,
        protected unfinishedHttpMethodsService: UnfinishedHttpMethodsService,
        protected portalContextService: PortalContextService,
        protected dialogService: DialogService,
        protected sectionService: SectionService,
        protected stateService: StateService,
        protected anfrageService: AnfrageService,
        protected ckeditorService: CKEditorService) {
        super(sectionService, router, route, portalContextService)

        this.reviewForm = GutachtenDisplayModelFormGroup.Instance;

    }

    public attachmentProcessStepForm: UntypedFormGroup = new UntypedFormGroup({
        attachments: new UntypedFormControl(null, []),
    });

    public get attachmentsFormControl(): UntypedFormControl {
        return <UntypedFormControl>this.attachmentProcessStepForm.controls["attachments"];
    }

    public override get canSave(): boolean {
        return !this.reviewForm.disabled;
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.review = this.route.snapshot.data.gutachten;
        this.reviewForm.patchValue(this.portalContextService.CurrentGutachten || this.review);

        if (this.review.anfrage?.attachments != null)
            this.attachmentProcessStepForm.patchValue(this.review.anfrage);
        this.attachmentsFormControl.disable();

        if (this.stateService.IsEndvalidationEnabled) {
            this.reviewForm.InhaltlicheZusammenfassung.addValidators(Validators.required);
            this.reviewForm.StaerkenSchwaechenAnalyse.addValidators(Validators.required);
            this.reviewForm.Bewertung.addValidators(Validators.required);
            this.reviewForm.Ergebnis.addValidators(Validators.required);
          
            this.reviewForm.markAllAsTouched();
        }
    }

    public override save(shouldSave: boolean, moveDirection: MoveDirection): void {
        if (!this.reviewForm.disabled && shouldSave && this.reviewForm.dirty) {
            let reviewData: GutachtenDisplayModel = this.reviewForm.value;
            let cleanReviewData: GutachtenDisplayModel = new GutachtenDisplayModel();
            cleanReviewData.gutachtenId = reviewData.gutachtenId || this.review.gutachtenId;
            cleanReviewData.inhaltlicheZusammenfassung = reviewData.inhaltlicheZusammenfassung;
            cleanReviewData.staerkenSchwaechenAnalyse = reviewData.staerkenSchwaechenAnalyse;
            cleanReviewData.ergebnis = reviewData.ergebnis;
            cleanReviewData.bewertung = reviewData.bewertung;
            cleanReviewData.statuscode = reviewData.statuscode || this.review.statuscode;

            this.portalContextService.CurrentGutachten!.inhaltlicheZusammenfassung = cleanReviewData.inhaltlicheZusammenfassung;
            this.portalContextService.CurrentGutachten!.staerkenSchwaechenAnalyse = cleanReviewData.staerkenSchwaechenAnalyse;
            this.portalContextService.CurrentGutachten!.ergebnis = cleanReviewData.ergebnis;
            this.portalContextService.CurrentGutachten!.bewertung = cleanReviewData.bewertung;
            this.portalContextService.CurrentGutachten!.statuscode = cleanReviewData.statuscode;

            this.reviewForm.markAsPristine();
            if (moveDirection != MoveDirection.STAY) {
                if (this.canceledRoute) {
                    this.router.navigateByUrl(this.canceledRoute);
                } else {
                    this.move(moveDirection);
                }
            }

            let subscription = this.gutachtenService.apiGutachtenUpdateGutachtenPatch({ body: cleanReviewData })
                .subscribe(() => {
                    this.gutachtenService.apiGutachtenGet({ gutachtenId: this.portalContextService.CurrentGutachten!.gutachtenId! })
                        .subscribe(response => {
                            if (response) {
                                this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.portalContextService.CurrentAnfrage?.id, reportId: this.portalContextService.CurrentBericht?.projektberichtId, gutachtenId: this.portalContextService.CurrentGutachten?.gutachtenId! }).subscribe(result => {
                                    this.stateService.ValidationErrors = result;
                                })
                                if (this.portalContextService.CurrentAnfrage)
                                    this.portalContextService.CurrentGutachten = response;

                                this.reviewForm.patchValue(response);
                                this.reviewForm.markAsPristine();
                            }
                        });
                });

            this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
            this.unfinishedHttpMethodsService.addSubscription(subscription);
        }
        else {
            this.reviewForm.markAsPristine();
            this.move(moveDirection);
        }
    }

    public get formGroup(): FormGroup<any> {
        return this.reviewForm;
    }

    public Editor = CustomEditor;
    public editorConfig = Constants.EDITOR_CONFIG;
    public gutachtenConfig = Constants.GUTACHTEN;
    
    public get bewertungList(): EnumValue[] {
        return GlobalEnumsAceBewertung.getAsDataset();
    }

    public initCKEditor(editor: any, containerId: string) {
        if(containerId == "word-count-inhaltlicheZusammenfassung") {
            this.ckeditorService.rows(4).initWordCount(editor, containerId, this.gutachtenConfig.INHALTLICHEZUSAMMENFASSUNG_MAXLENGTH);
            this.reviewForm.InhaltlicheZusammenfassung.valueChanges.subscribe(() => {
              this.ckeditorService.validateLength(editor, this.gutachtenConfig.INHALTLICHEZUSAMMENFASSUNG_MAXLENGTH);
            })
        }
        if(containerId == "word-count-staerkenSchwaechenAnalyse") {
            this.ckeditorService.rows(4).initWordCount(editor, containerId, this.gutachtenConfig.STAERKENSCHWAECHENANALYSE_MAXLENGTH);
            this.reviewForm.StaerkenSchwaechenAnalyse.valueChanges.subscribe(() => {
              this.ckeditorService.validateLength(editor, this.gutachtenConfig.STAERKENSCHWAECHENANALYSE_MAXLENGTH);
            })
        }
        if(containerId == "word-count-ergebnis") {
            this.ckeditorService.rows(4).initWordCount(editor, containerId, this.gutachtenConfig.ERGEBNIS_MAXLENGTH);
            this.reviewForm.Ergebnis.valueChanges.subscribe(() => {
              this.ckeditorService.validateLength(editor, this.gutachtenConfig.ERGEBNIS_MAXLENGTH);
            })
        }
    }
}